import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ESimplifiedBuyingInjection } from "features/eSimplifiedBuyingBeta/di/ESimplifiedBuyingInjection";
import { injectable } from "tsyringe";
import { OrderSimDataFormRequestEntity } from "../entities/OrderSimDataFormRequestEntity";
import {
  OrderSimDataCustomerEntity,
  OrderSimDataHeaderEntity,
  OrderSimDataRequestEntity,
} from "../entities/OrderSimDataRequestEntity";
import { OrderSimDataResponseEntity } from "../entities/OrderSimDataResponseEntity";
import { IESimplifiedBuyingRepository } from "../repositories/IESimplifiedBuyingRepository";

@injectable()
export class ESimplifiedBuyingUseCase {
  private eSimplifiedBuyingRepo: IESimplifiedBuyingRepository;

  constructor() {
    this.eSimplifiedBuyingRepo = ESimplifiedBuyingInjection.getRepo();
  }

  private buildSignature(params: OrderSimDataFormRequestEntity): string {
    const payload = [
      params?.partnerKey ?? "",
      params?.partnerKeyPass ?? "",
      params?.transRef ?? "",
      params?.packageCode ?? "",
      params?.simType ?? "",
    ].join("|");

    return this.md5(payload);
  }

  private buildOrderSimDataRequest(
    params: OrderSimDataFormRequestEntity
  ): OrderSimDataRequestEntity {
    return new OrderSimDataRequestEntity({
      transRef: params?.transRef,
      packageCode: params?.packageCode,
      simType: params?.simType,
      phoneNumberCode: params?.phoneNumberCode,
      invoice: params?.invoice,
      customer: new OrderSimDataCustomerEntity({
        name: params?.customerName,
        phone: params?.customerPhone,
        email: params?.customerEmail,
        address: params?.customerAddress,
      }),
    });
  }

  private buildOrderSimDataHeaders(
    params: OrderSimDataFormRequestEntity
  ): OrderSimDataHeaderEntity {
    return new OrderSimDataHeaderEntity({
      partnerKey: params?.partnerKey,
      signature: this.buildSignature(params),
    });
  }

  async orderSimData(
    params: OrderSimDataFormRequestEntity
  ): Promise<BaseApiResponseModel<OrderSimDataResponseEntity>> {
    return await this.eSimplifiedBuyingRepo.orderSimData(
      this.buildOrderSimDataRequest(params),
      this.buildOrderSimDataHeaders(params)
    );
  }

  private md5(input: string): string {
    const utf8Input = unescape(encodeURIComponent(input));
    const words = this.convertToWordArray(utf8Input);
    let a = 0x67452301;
    let b = 0xefcdab89;
    let c = 0x98badcfe;
    let d = 0x10325476;

    for (let i = 0; i < words.length; i += 16) {
      const aa = a;
      const bb = b;
      const cc = c;
      const dd = d;

      a = this.ff(a, b, c, d, words[i + 0], 7, 0xd76aa478);
      d = this.ff(d, a, b, c, words[i + 1], 12, 0xe8c7b756);
      c = this.ff(c, d, a, b, words[i + 2], 17, 0x242070db);
      b = this.ff(b, c, d, a, words[i + 3], 22, 0xc1bdceee);
      a = this.ff(a, b, c, d, words[i + 4], 7, 0xf57c0faf);
      d = this.ff(d, a, b, c, words[i + 5], 12, 0x4787c62a);
      c = this.ff(c, d, a, b, words[i + 6], 17, 0xa8304613);
      b = this.ff(b, c, d, a, words[i + 7], 22, 0xfd469501);
      a = this.ff(a, b, c, d, words[i + 8], 7, 0x698098d8);
      d = this.ff(d, a, b, c, words[i + 9], 12, 0x8b44f7af);
      c = this.ff(c, d, a, b, words[i + 10], 17, 0xffff5bb1);
      b = this.ff(b, c, d, a, words[i + 11], 22, 0x895cd7be);
      a = this.ff(a, b, c, d, words[i + 12], 7, 0x6b901122);
      d = this.ff(d, a, b, c, words[i + 13], 12, 0xfd987193);
      c = this.ff(c, d, a, b, words[i + 14], 17, 0xa679438e);
      b = this.ff(b, c, d, a, words[i + 15], 22, 0x49b40821);

      a = this.gg(a, b, c, d, words[i + 1], 5, 0xf61e2562);
      d = this.gg(d, a, b, c, words[i + 6], 9, 0xc040b340);
      c = this.gg(c, d, a, b, words[i + 11], 14, 0x265e5a51);
      b = this.gg(b, c, d, a, words[i + 0], 20, 0xe9b6c7aa);
      a = this.gg(a, b, c, d, words[i + 5], 5, 0xd62f105d);
      d = this.gg(d, a, b, c, words[i + 10], 9, 0x02441453);
      c = this.gg(c, d, a, b, words[i + 15], 14, 0xd8a1e681);
      b = this.gg(b, c, d, a, words[i + 4], 20, 0xe7d3fbc8);
      a = this.gg(a, b, c, d, words[i + 9], 5, 0x21e1cde6);
      d = this.gg(d, a, b, c, words[i + 14], 9, 0xc33707d6);
      c = this.gg(c, d, a, b, words[i + 3], 14, 0xf4d50d87);
      b = this.gg(b, c, d, a, words[i + 8], 20, 0x455a14ed);
      a = this.gg(a, b, c, d, words[i + 13], 5, 0xa9e3e905);
      d = this.gg(d, a, b, c, words[i + 2], 9, 0xfcefa3f8);
      c = this.gg(c, d, a, b, words[i + 7], 14, 0x676f02d9);
      b = this.gg(b, c, d, a, words[i + 12], 20, 0x8d2a4c8a);

      a = this.hh(a, b, c, d, words[i + 5], 4, 0xfffa3942);
      d = this.hh(d, a, b, c, words[i + 8], 11, 0x8771f681);
      c = this.hh(c, d, a, b, words[i + 11], 16, 0x6d9d6122);
      b = this.hh(b, c, d, a, words[i + 14], 23, 0xfde5380c);
      a = this.hh(a, b, c, d, words[i + 1], 4, 0xa4beea44);
      d = this.hh(d, a, b, c, words[i + 4], 11, 0x4bdecfa9);
      c = this.hh(c, d, a, b, words[i + 7], 16, 0xf6bb4b60);
      b = this.hh(b, c, d, a, words[i + 10], 23, 0xbebfbc70);
      a = this.hh(a, b, c, d, words[i + 13], 4, 0x289b7ec6);
      d = this.hh(d, a, b, c, words[i + 0], 11, 0xeaa127fa);
      c = this.hh(c, d, a, b, words[i + 3], 16, 0xd4ef3085);
      b = this.hh(b, c, d, a, words[i + 6], 23, 0x04881d05);
      a = this.hh(a, b, c, d, words[i + 9], 4, 0xd9d4d039);
      d = this.hh(d, a, b, c, words[i + 12], 11, 0xe6db99e5);
      c = this.hh(c, d, a, b, words[i + 15], 16, 0x1fa27cf8);
      b = this.hh(b, c, d, a, words[i + 2], 23, 0xc4ac5665);

      a = this.ii(a, b, c, d, words[i + 0], 6, 0xf4292244);
      d = this.ii(d, a, b, c, words[i + 7], 10, 0x432aff97);
      c = this.ii(c, d, a, b, words[i + 14], 15, 0xab9423a7);
      b = this.ii(b, c, d, a, words[i + 5], 21, 0xfc93a039);
      a = this.ii(a, b, c, d, words[i + 12], 6, 0x655b59c3);
      d = this.ii(d, a, b, c, words[i + 3], 10, 0x8f0ccc92);
      c = this.ii(c, d, a, b, words[i + 10], 15, 0xffeff47d);
      b = this.ii(b, c, d, a, words[i + 1], 21, 0x85845dd1);
      a = this.ii(a, b, c, d, words[i + 8], 6, 0x6fa87e4f);
      d = this.ii(d, a, b, c, words[i + 15], 10, 0xfe2ce6e0);
      c = this.ii(c, d, a, b, words[i + 6], 15, 0xa3014314);
      b = this.ii(b, c, d, a, words[i + 13], 21, 0x4e0811a1);
      a = this.ii(a, b, c, d, words[i + 4], 6, 0xf7537e82);
      d = this.ii(d, a, b, c, words[i + 11], 10, 0xbd3af235);
      c = this.ii(c, d, a, b, words[i + 2], 15, 0x2ad7d2bb);
      b = this.ii(b, c, d, a, words[i + 9], 21, 0xeb86d391);

      a = this.addUnsigned(a, aa);
      b = this.addUnsigned(b, bb);
      c = this.addUnsigned(c, cc);
      d = this.addUnsigned(d, dd);
    }

    return `${this.wordToHex(a)}${this.wordToHex(b)}${this.wordToHex(
      c
    )}${this.wordToHex(d)}`.toLowerCase();
  }

  private rotateLeft(value: number, shift: number): number {
    return (value << shift) | (value >>> (32 - shift));
  }

  private addUnsigned(x: number, y: number): number {
    const x4 = x & 0x40000000;
    const y4 = y & 0x40000000;
    const x8 = x & 0x80000000;
    const y8 = y & 0x80000000;
    const result = (x & 0x3fffffff) + (y & 0x3fffffff);

    if (x4 & y4) return result ^ 0x80000000 ^ x8 ^ y8;
    if (x4 | y4) {
      if (result & 0x40000000) return result ^ 0xc0000000 ^ x8 ^ y8;
      return result ^ 0x40000000 ^ x8 ^ y8;
    }

    return result ^ x8 ^ y8;
  }

  private f(x: number, y: number, z: number): number {
    return (x & y) | (~x & z);
  }

  private g(x: number, y: number, z: number): number {
    return (x & z) | (y & ~z);
  }

  private h(x: number, y: number, z: number): number {
    return x ^ y ^ z;
  }

  private i(x: number, y: number, z: number): number {
    return y ^ (x | ~z);
  }

  private convertRound(
    q: number,
    a: number,
    b: number,
    x: number,
    s: number,
    ac: number
  ): number {
    return this.addUnsigned(
      this.rotateLeft(
        this.addUnsigned(this.addUnsigned(a, q), this.addUnsigned(x, ac)),
        s
      ),
      b
    );
  }

  private ff(
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ): number {
    return this.convertRound(this.f(b, c, d), a, b, x, s, ac);
  }

  private gg(
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ): number {
    return this.convertRound(this.g(b, c, d), a, b, x, s, ac);
  }

  private hh(
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ): number {
    return this.convertRound(this.h(b, c, d), a, b, x, s, ac);
  }

  private ii(
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ): number {
    return this.convertRound(this.i(b, c, d), a, b, x, s, ac);
  }

  private convertToWordArray(input: string): number[] {
    const messageLength = input.length;
    const numberOfWords =
      ((messageLength + 8 - ((messageLength + 8) % 64)) / 64 + 1) * 16;
    const words = new Array<number>(numberOfWords - 1);
    let bytePosition = 0;

    for (let byteCount = 0; byteCount < messageLength; byteCount += 1) {
      const wordCount = (byteCount - (byteCount % 4)) / 4;
      bytePosition = (byteCount % 4) * 8;
      words[wordCount] =
        (words[wordCount] || 0) | (input.charCodeAt(byteCount) << bytePosition);
    }

    const wordCount = (messageLength - (messageLength % 4)) / 4;
    bytePosition = (messageLength % 4) * 8;
    words[wordCount] = (words[wordCount] || 0) | (0x80 << bytePosition);
    words[numberOfWords - 2] = messageLength << 3;
    words[numberOfWords - 1] = messageLength >>> 29;

    return words;
  }

  private wordToHex(value: number): string {
    let wordToHexValue = "";

    for (let count = 0; count <= 3; count += 1) {
      const byte = (value >>> (count * 8)) & 255;
      wordToHexValue += `0${byte.toString(16)}`.slice(-2);
    }

    return wordToHexValue;
  }
}
